/**
 * オフラインファースト URL 解決フック
 *
 * IndexedDB にオフラインBlobがあればそのObjectURLを返し、
 * なければ通常の署名付きURLにフォールバックする。
 */
import { useEffect, useRef, useState } from 'react'

import { getOfflineEpisode } from '@/lib/db'

import { useSignedUrl } from './useSignedUrl'

type UseOfflineUrlResult = {
  /** 利用可能なURL（ObjectURL or 署名付きURL） */
  url: string | null
  /** オフラインBlobから提供されているか */
  isOffline: boolean
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * エピソードIDとストリームパスを受け取り、オフライン→オンラインの順でURLを解決する
 *
 * @param episodeId エピソードID（数値）。nullの場合はスキップ
 * @param streamPath オンラインストリームパス（例: `/e/123/stream`）。オフライン不在時のフォールバック
 */
export const useOfflineUrl = (episodeId: number | null, streamPath: string | null): UseOfflineUrlResult => {
  const [offlineUrl, setOfflineUrl] = useState<string | null>(null)
  const [isOffline, setIsOffline] = useState(false)
  const [checking, setChecking] = useState(true)
  const objectUrlRef = useRef<string | null>(null)

  // 署名付きURL（オフラインが無い場合のフォールバック）
  // オフライン確認中 or オフラインBlobがある場合はfetchしない
  const signedUrlPath = !isOffline && !checking ? streamPath : null
  const { url: signedUrl, isLoading: signedLoading, error: signedError, refetch } = useSignedUrl(signedUrlPath)

  // IndexedDB をチェック
  useEffect(() => {
    if (episodeId === null) {
      setChecking(false)
      return
    }

    let cancelled = false

    const check = async () => {
      try {
        const ep = await getOfflineEpisode(episodeId)
        if (cancelled) return

        if (ep) {
          // 前回の ObjectURL をクリーンアップ
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current)
          }
          const blobUrl = URL.createObjectURL(ep.blob)
          objectUrlRef.current = blobUrl
          setOfflineUrl(blobUrl)
          setIsOffline(true)
        } else {
          setIsOffline(false)
          setOfflineUrl(null)
        }
      } catch {
        setIsOffline(false)
        setOfflineUrl(null)
      } finally {
        if (!cancelled) setChecking(false)
      }
    }

    setChecking(true)
    check()

    return () => {
      cancelled = true
    }
  }, [episodeId])

  // ObjectURL のクリーンアップ（アンマウント時）
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [])

  // オフラインBlobが利用可能ならそちらを返す
  if (isOffline && offlineUrl) {
    return {
      url: offlineUrl,
      isOffline: true,
      isLoading: false,
      error: null,
      refetch,
    }
  }

  return {
    url: signedUrl,
    isOffline: false,
    isLoading: checking || signedLoading,
    error: signedError,
    refetch,
  }
}
